import {
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Transaction } from '@elastic/apm-rum';
import { ApmService } from '@elastic/apm-rum-angular';
import { finalize, tap } from 'rxjs';

const SENSITIVE_QUERY_PARAM_PATTERN =
    /pass(word)?|pwd|token|secret|authorization|auth|api[-_]?key|access[-_]?token|refresh[-_]?token|session/i;
const STATIC_ASSET_REQUEST_PATTERN =
    /(^|\/)(assets|icons)\/|\.(?:css|js|json|map|png|jpe?g|gif|svg|ico|woff2?|ttf|webp|webmanifest)$/i;
const textEncoder = new TextEncoder();

/**
 * Tracks every outgoing API call in Elastic APM with a dedicated custom transaction.
 *
 * Labels intentionally avoid request/response bodies. Only safe metadata is stored.
 */
export const apmApiTrackingInterceptor: HttpInterceptorFn = (req, next) => {
    const apmService = inject(ApmService);

    if (!shouldTrackRequest(req.url) || !apmService.apm.isActive()) {
        return next(req);
    }

    const sanitizedUrl = sanitizeUrl(req.url);
    const payloadSize = getRequestPayloadSize(req.body);
    const startedAt = performance.now();
    const transaction = apmService.apm.startTransaction(
        buildTransactionName(req.method, sanitizedUrl),
        'http-request'
    );

    let responseStatusCode: number | undefined;
    let requestSucceeded = false;

    return next(req).pipe(
        tap({
            next: (event: HttpEvent<unknown>) => {
                if (event.type === HttpEventType.Response) {
                    responseStatusCode = event.status;
                    requestSucceeded = true;
                }
            },
            error: (error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    responseStatusCode = error.status;
                }

                captureRequestError(apmService, error, {
                    sanitizedUrl,
                    method: req.method,
                    payloadSize,
                    responseTimeMs: roundMetric(performance.now() - startedAt),
                    responseStatusCode,
                });
            },
        }),
        finalize(() => {
            finalizeTransaction(transaction, {
                sanitizedUrl,
                method: req.method,
                payloadSize,
                responseTimeMs: roundMetric(performance.now() - startedAt),
                responseStatusCode,
                requestSucceeded,
            });
        })
    );
};

function shouldTrackRequest(url: string): boolean {
    if (/SentPacketLog\/(AddTempSentPacket|AddTempSentPacketLog)/i.test(url)) {
        return false;
    }
    return !STATIC_ASSET_REQUEST_PATTERN.test(url);
}

function buildTransactionName(method: string, sanitizedUrl: string): string {
    try {
        const parsedUrl = new URL(sanitizedUrl, window.location.origin);
        return `${method.toUpperCase()} ${parsedUrl.pathname}`;
    } catch {
        return `${method.toUpperCase()} ${sanitizedUrl}`;
    }
}

function sanitizeUrl(url: string): string {
    try {
        const parsedUrl = new URL(url, window.location.origin);

        parsedUrl.searchParams.forEach((value, key) => {
            if (SENSITIVE_QUERY_PARAM_PATTERN.test(key)) {
                parsedUrl.searchParams.set(key, '[REDACTED]');
            } else if (looksLikeToken(value)) {
                parsedUrl.searchParams.set(key, '[REDACTED]');
            }
        });

        parsedUrl.hash = '';
        return `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
        return url;
    }
}

function looksLikeToken(value: string): boolean {
    return /^bearer\s+/i.test(value) || value.length > 64;
}

function getRequestPayloadSize(body: unknown): number {
    if (body == null) {
        return 0;
    }

    if (typeof body === 'string') {
        return textEncoder.encode(body).length;
    }

    if (body instanceof Blob) {
        return body.size;
    }

    if (body instanceof ArrayBuffer) {
        return body.byteLength;
    }

    if (ArrayBuffer.isView(body)) {
        return body.byteLength;
    }

    if (body instanceof FormData) {
        let formDataSize = 0;

        body.forEach((value, key) => {
            formDataSize += textEncoder.encode(key).length;
            formDataSize += typeof value === 'string' ? textEncoder.encode(value).length : value.size;
        });

        return formDataSize;
    }

    try {
        return textEncoder.encode(JSON.stringify(body)).length;
    } catch {
        return 0;
    }
}

function captureRequestError(
    apmService: ApmService,
    error: unknown,
    requestMetrics: RequestMetrics
): void {
    const errorToCapture = error instanceof Error ? error : new Error('HTTP request failed');

    apmService.apm.captureError(errorToCapture, {
        labels: {
            api_url: requestMetrics.sanitizedUrl,
            http_method: requestMetrics.method.toUpperCase(),
            request_payload_size_bytes: requestMetrics.payloadSize,
            response_time_ms: requestMetrics.responseTimeMs,
            http_status_code: requestMetrics.responseStatusCode ?? 0,
            request_outcome: 'failure',
        },
    });
}

function finalizeTransaction(
    transaction: Transaction | undefined,
    requestMetrics: RequestMetrics & { requestSucceeded: boolean }
): void {
    if (!transaction || transaction.isFinished()) {
        return;
    }

    transaction.addLabels({
        api_url: requestMetrics.sanitizedUrl,
        http_method: requestMetrics.method.toUpperCase(),
        request_payload_size_bytes: requestMetrics.payloadSize,
        response_time_ms: requestMetrics.responseTimeMs,
        http_status_code: requestMetrics.responseStatusCode ?? 0,
        request_outcome: requestMetrics.requestSucceeded ? 'success' : 'failure',
    });

    transaction.end();
}

function roundMetric(value: number): number {
    return Number(value.toFixed(2));
}

interface RequestMetrics {
    sanitizedUrl: string;
    method: string;
    payloadSize: number;
    responseTimeMs: number;
    responseStatusCode?: number;
}